// The charts Analytics -> Reporting offers in its picker (owner, 2026-11-03:
// "take the many charts that we have built currently for Dashboard and move
// them to Reporting ... with a picker at the top").
//
// ONE ENTRY PER CHART, and each entry says everything the screen and the two
// export formats need: which read it draws from, its title, the heading of its
// value column, and which filter a bar click toggles. The screen draws the
// entry; `chartExport.ts` writes the same rows to PDF and Excel, so what is on
// screen and what is in the file cannot drift apart.
//
// EVERY ROW, NOT A TOP TEN. The Dashboard kept ten bars per panel because
// thirty panels shared one page. Reporting shows one chart at a time across the
// whole width, so it shows every row the server returned. The server still
// keeps its own cap (the top 50 on each measure, `truncated` says when it bit),
// and the screen says so when it does.
//
// No I/O here: the screen does the reads and hands the results in.
import type {
  AnalyticsByDimension,
  AnalyticsCorrections,
  AnalyticsDimensionRow,
  AnalyticsLineItems,
  AnalyticsSpendByCode,
  AnalyticsSpendByEntity,
} from '../api/types'
import type { BarRowData } from '../components/DashboardCharts'
import { STEP_LABEL, TIMELINE_NODES, flagLabel } from './constants'
import { correctionFieldLabel } from './metrics'
import { SECTION_COLORS } from './theme'

/** Every read the charts draw from. A failed read is `null` and its charts say so. */
export interface ChartData {
  step: AnalyticsByDimension | null
  vendor: AnalyticsByDimension | null
  company: AnalyticsByDimension | null
  accounts: AnalyticsSpendByCode | null
  departments: AnalyticsSpendByCode | null
  codes: AnalyticsSpendByCode | null
  entities: AnalyticsSpendByEntity | null
  corrections: AnalyticsCorrections | null
  /**
   * `GET /analytics/line-items` (§16.14), behind Line Items by Spend, by
   * Vendor and by Vehicle. Optional until the screen reads it: absent draws
   * those three as a failed read.
   */
  lineItems?: AnalyticsLineItems | null
}

/** The criteria a bar click can toggle. */
type ChartFilterKey = 'companies' | 'vendors' | 'accounts' | 'departments' | 'codes' | 'statuses'

export interface ReportChart {
  id: string
  /** The picker's group heading. */
  group: string
  title: string
  /** The heading over the label column in the exported table. */
  labelHeader: string
  /** The heading over the value column in the exported table. */
  valueHeader: string
  /** What a bar click toggles; absent where no criterion matches the bar. */
  filter?: ChartFilterKey
  /** Rows, largest first, or `null` when the read behind them failed. */
  rows: (data: ChartData) => BarRowData[] | null
  /** True when the server left rows out of the read behind this chart. */
  truncated: (data: ChartData) => boolean
  /** Wider value column for a display longer than a count ("62% of 13"). */
  valueWidth?: number
}

/** The server's stand-in key for a row whose dimension column is null. Not a
    filterable value — "vendor is (none)" is not a question the contract asks. */
export const NO_KEY = '(none)'

const compactUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
})
const weekLabel = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

const whole = (value: number) => String(Math.round(value))
const money = (value: number) => compactUsd.format(value)
const dayCount = (value: number) => `${value} d`
/** `45 s` under a minute, `2m 05s` above it — a median nobody reads as exact. */
const seconds = (value: number) => {
  const total = Math.round(value)
  if (total < 60) return `${total} s`
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`
}
/** A median of flags per invoice: whole when it is, one place when it falls between two. */
const flagMedian = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1))

/** Node key -> the label the timeline prints. A key with no node keeps itself. */
const NODE_LABEL = new Map<string, string>(TIMELINE_NODES.map((node) => [node.key, node.label]))
export const stepLabel = (row: AnalyticsDimensionRow) => NODE_LABEL.get(row.key) ?? STEP_LABEL[row.key] ?? row.key
const plainLabel = (row: AnalyticsDimensionRow) => row.label

/** Rows sorted by one measure, zeros dropped, turned into bars of one colour. */
function dimensionBars(
  rows: readonly AnalyticsDimensionRow[],
  measure: (row: AnalyticsDimensionRow) => number,
  display: (value: number) => string,
  color: string,
  label: (row: AnalyticsDimensionRow) => string,
): BarRowData[] {
  return [...rows]
    .filter((row) => measure(row) > 0)
    .sort((a, b) => measure(b) - measure(a))
    .map((row) => ({
      key: row.key,
      label: label(row),
      total: measure(row),
      display: display(measure(row)),
      segments: [{ color, value: measure(row) }],
      title: `${label(row)}: ${display(measure(row))}`,
    }))
}

/** Plain `{ key, label, value }` rows as bars, largest first. */
function countBars(rows: { key: string; label: string; value: number }[], color: string, show = whole): BarRowData[] {
  return rows
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value)
    .map((row) => ({
      key: row.key,
      label: row.label,
      total: row.value,
      display: show(row.value),
      segments: [{ color, value: row.value }],
      title: `${row.label}: ${show(row.value)}`,
    }))
}

function spendBars(breakdown: AnalyticsSpendByCode | null): BarRowData[] | null {
  if (breakdown === null) return null
  return [...breakdown.rows]
    .filter((row) => row.total > 0)
    .sort((a, b) => b.total - a.total)
    .map((row) => ({
      key: row.key,
      label: row.label,
      total: row.total,
      display: money(row.total),
      segments: [{ color: SECTION_COLORS.spend, value: row.total }],
      title: `${row.label}: ${money(row.total)} across ${row.count} invoice${row.count === 1 ? '' : 's'}`,
    }))
}

// A null median is "nothing has finished here yet". It is NOT zero, and
// treating it as zero would draw a bar claiming instant turnaround;
// `dimensionBars` drops a row measuring 0, so a null row simply does not appear.
// Handling time is how long a person WORKED on it, not how long it SAT.
const MEASURES = [
  { word: 'Invoices', title: (noun: string) => `Invoices Per ${noun}`, header: 'Invoices', measure: (r: AnalyticsDimensionRow) => r.invoice_count, show: whole },
  { word: 'Spend', title: (noun: string) => `Spend Per ${noun}`, header: 'Spend', measure: (r: AnalyticsDimensionRow) => r.total, show: money },
  { word: 'Days', title: (noun: string) => `Median Days Per ${noun}`, header: 'Median Days', measure: (r: AnalyticsDimensionRow) => r.median_days ?? 0, show: dayCount },
  { word: 'Handling', title: (noun: string) => `Median Handling Time Per ${noun}`, header: 'Median Handling Seconds', measure: (r: AnalyticsDimensionRow) => r.median_handling_seconds ?? 0, show: seconds },
  { word: 'Opened', title: (noun: string) => `Times Opened Per ${noun}`, header: 'Times Opened', measure: (r: AnalyticsDimensionRow) => r.touch_count, show: whole },
  { word: 'Denials', title: (noun: string) => `Denials Per ${noun}`, header: 'Denials', measure: (r: AnalyticsDimensionRow) => r.denial_count, show: whole },
] as const

const DIMENSIONS = [
  { source: 'step', noun: 'Step', group: 'Per Step', color: SECTION_COLORS.step, label: stepLabel, filter: 'statuses' },
  { source: 'vendor', noun: 'Vendor', group: 'Per Vendor', color: SECTION_COLORS.vendor, label: plainLabel, filter: 'vendors' },
  { source: 'company', noun: 'Company', group: 'Per Company', color: SECTION_COLORS.company, label: plainLabel, filter: 'companies' },
] as const

const dimensionCharts: ReportChart[] = DIMENSIONS.flatMap((dim) =>
  MEASURES.map((m) => ({
    id: `${dim.source}-${m.word.toLowerCase()}`,
    group: dim.group,
    title: m.title(dim.noun),
    labelHeader: dim.noun,
    valueHeader: m.header,
    filter: dim.filter,
    rows: (data: ChartData) => {
      const breakdown = data[dim.source]
      return breakdown === null ? null : dimensionBars(breakdown.rows, m.measure, m.show, dim.color, dim.label)
    },
    truncated: (data: ChartData) => data[dim.source]?.truncated === true,
  })),
)

const spendChart = (
  id: string,
  title: string,
  noun: string,
  source: 'accounts' | 'departments' | 'codes',
): ReportChart => ({
  id,
  group: 'By Spend',
  title,
  labelHeader: noun,
  valueHeader: 'Spend',
  // Account, Department and Code are the trailing half, the leading half and
  // the whole code the two compose (D-85), so each bar toggles its namesake.
  filter: source,
  rows: (data) => spendBars(data[source]),
  truncated: (data) => data[source]?.truncated === true,
})

const feedback = SECTION_COLORS.feedback

export const REPORT_CHARTS: readonly ReportChart[] = [
  ...dimensionCharts,
  spendChart('accounts-spend', 'Accounts by Spend', 'Account', 'accounts'),
  spendChart('departments-spend', 'Departments by Spend', 'Department', 'departments'),
  spendChart('codes-spend', 'Codes by Spend', 'Code', 'codes'),
  {
    // NOT CLICKABLE: an associated entity is a vehicle, a staff member or a
    // participant, and the criteria have no field for one.
    id: 'entities-spend',
    group: 'By Spend',
    title: 'Entities by Spend',
    labelHeader: 'Entity',
    valueHeader: 'Spend',
    rows: (data) =>
      data.entities === null
        ? null
        : [...data.entities.entities]
            .filter((row) => row.total > 0)
            .sort((a, b) => b.total - a.total)
            .map((row) => ({
              key: `${row.entity_type}|${row.entity_key}`,
              label: row.entity_name ?? row.entity_key,
              total: row.total,
              display: money(row.total),
              segments: [{ color: SECTION_COLORS.spend, value: row.total }],
              title: `${row.entity_name ?? row.entity_key}: ${money(row.total)}`,
            })),
    truncated: (data) => data.entities?.truncated === true,
  },
  {
    id: 'corrections-vendor',
    group: 'Corrections, Flags, and Holds',
    title: 'Corrections Per Vendor',
    labelHeader: 'Vendor',
    valueHeader: 'Corrections',
    filter: 'vendors',
    rows: (data) =>
      data.corrections === null
        ? null
        : countBars(
            data.corrections.by_vendor.map((row) => ({
              key: row.vendor_id ?? NO_KEY,
              label: row.vendor_name ?? row.vendor_id ?? '(no vendor)',
              value: row.count,
            })),
            feedback,
          ),
    truncated: () => false,
  },
  {
    // The field's display name, not its column name — the same map Decision
    // History reads. NOT CLICKABLE: there is no corrected-field criterion.
    id: 'corrections-field',
    group: 'Corrections, Flags, and Holds',
    title: 'Corrections Per Field',
    labelHeader: 'Field',
    valueHeader: 'Corrections',
    rows: (data) =>
      data.corrections === null
        ? null
        : countBars(
            data.corrections.by_field.map((row) => ({
              key: row.field,
              label: correctionFieldLabel(row.field),
              value: row.count,
            })),
            feedback,
          ),
    truncated: () => false,
  },
  {
    id: 'flags-vendor',
    group: 'Corrections, Flags, and Holds',
    title: 'Flags Per Vendor',
    labelHeader: 'Vendor',
    valueHeader: 'Flags',
    filter: 'vendors',
    rows: (data) =>
      data.corrections === null
        ? null
        : countBars(
            data.corrections.flags.by_vendor.map((row) => ({
              key: row.vendor_id ?? NO_KEY,
              label: row.vendor_name ?? row.vendor_id ?? '(no vendor)',
              value: row.count,
            })),
            feedback,
          ),
    truncated: () => false,
  },
  {
    // One bar per week received, OLDEST FIRST — time, not rank, so no sort.
    id: 'flags-median-week',
    group: 'Corrections, Flags, and Holds',
    title: 'Median Flags Per Invoice',
    labelHeader: 'Week',
    valueHeader: 'Median Flags Per Invoice',
    rows: (data) =>
      data.corrections === null
        ? null
        : data.corrections.flags.median_by_week.map((row) => ({
            key: row.week_start,
            label: `Week of ${weekLabel.format(new Date(`${row.week_start}T00:00:00Z`))}`,
            total: row.median,
            display: flagMedian(row.median),
            segments: [{ color: feedback, value: row.median }],
            title: `${flagMedian(row.median)} flags per invoice, median of ${row.invoice_count} invoice${row.invoice_count === 1 ? '' : 's'}`,
          })),
    truncated: () => false,
  },
  {
    // The server's label, from the flag's `flag_definitions` row: a retired
    // flag is in no live copy here (2026-10-22).
    id: 'flags-type',
    group: 'Corrections, Flags, and Holds',
    title: 'Flags Per Type',
    labelHeader: 'Flag',
    valueHeader: 'Flags',
    rows: (data) =>
      data.corrections === null
        ? null
        : countBars(
            data.corrections.flags.by_type.map((row) => ({
              key: row.flag_type,
              label: flagLabel(row.flag_type, row.label),
              value: row.count,
            })),
            feedback,
          ),
    truncated: () => false,
  },
  {
    // REQUIRED CORRECTIONS PER FLAG (D-368): per flag type, the share of its
    // RESOLVED flags that saw a person's correction on the same invoice (the
    // same line, for a line flag) between being raised and being resolved. The
    // server sends them highest share first.
    id: 'flags-needed-correction',
    group: 'Corrections, Flags, and Holds',
    title: 'Required Corrections Per Flag',
    labelHeader: 'Flag',
    valueHeader: 'Percent Corrected',
    valueWidth: 88,
    rows: (data) =>
      data.corrections === null
        ? null
        : data.corrections.flags.needed_correction_by_type
            .filter((row) => row.resolved_count > 0)
            .map((row) => {
              const percent = Math.round((row.corrected_count / row.resolved_count) * 100)
              const label = flagLabel(row.flag_type, row.label)
              return {
                key: row.flag_type,
                label,
                total: percent,
                display: `${percent}% of ${row.resolved_count}`,
                segments: [{ color: feedback, value: percent }],
                title:
                  `${label}: ${row.corrected_count} of ${row.resolved_count} resolved flags saw a correction ` +
                  'on their invoice (their line, for a line flag) between being raised and being resolved.',
              }
            }),
    truncated: () => false,
  },
  {
    id: 'holds-vendor',
    group: 'Corrections, Flags, and Holds',
    title: 'Holds Per Vendor',
    labelHeader: 'Vendor',
    valueHeader: 'Holds',
    filter: 'vendors',
    rows: (data) => (data.vendor === null ? null : dimensionBars(data.vendor.rows, (r) => r.hold_count, whole, feedback, plainLabel)),
    truncated: (data) => data.vendor?.truncated === true,
  },
  {
    id: 'holds-step',
    group: 'Corrections, Flags, and Holds',
    title: 'Holds Per Step',
    labelHeader: 'Step',
    valueHeader: 'Holds',
    filter: 'statuses',
    rows: (data) => (data.step === null ? null : dimensionBars(data.step.rows, (r) => r.hold_count, whole, feedback, stepLabel)),
    truncated: (data) => data.step?.truncated === true,
  },
]

export const DEFAULT_CHART_ID = 'step-invoices'


export { money as compactMoney, whole as wholeNumber }
